import { useContext, useEffect, useState } from "react";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import {
  batchDeleteInvoices,
  getInvoicesByCustomer,
  removeCustomerFromInvoices,
} from "../../../../database/helpers/invoice";
import {
  bulkDeletePayments,
  bulkDeletePaymentsV2,
  getPaymentsByCustomer,
} from "../../../../database/helpers/payment";
import {
  bulkDeletePosTxns,
  removeCustomerFromPosTxns,
} from "../../../../database/helpers/pos_txns";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { Sync } from "../../../../SyncService";
import { Box } from "@mui/material";
import { bulkDeleteOrdersByInvoices } from "../../../../database/helpers/orders";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";

const DeleteAccount = ({
  children,
  customer,
  onDeleteSuccess,
  open: openDeleteAccount,
  setOpen: setOpenDeleteAccount,
  id,
}) => {
  const { user } = useAuthContext();
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);
  const [confirmationDeleteInvoice, setConfirmationDeleteInvoice] =
    useState(false);

  useEffect(() => {
    if (openDeleteAccount) setOpen(openDeleteAccount);
  }, [openDeleteAccount]);

  const deleteCustomer = async (deleteInvoice = false) => {
    if (isLoadingDeletion) return;

    setIsLoadingDeletion(true);

    const handleSuccess = () => {
      triggerLogEvent(analytics, "acc_deleted", {
        customer_id: customer?.id,
        is_supplier: customer?.is_supplier || false,
      });
      showSnackbar({
        message: customer.is_supplier
          ? I18n.supplier_deleted_successfully
          : I18n.customer_deleted_successfully,
        severity: "success",
        toastId: "deleteAccountSuccess",
      });

      if (setOpenDeleteAccount) setOpenDeleteAccount(false);
      setOpen(false);
      setConfirmationDeleteInvoice(false);
      setIsLoadingDeletion(false);
      onDeleteSuccess();

      Sync(user.phone_number, navigate).catch(() => {});
    };

    const handleError = (err) => {
      showSnackbar({
        message: "Something went wrong while deleting customer",
        severity: "error",
        toastId: "deleteAccountError",
      });

      if (setOpenDeleteAccount) setOpenDeleteAccount(false);
      setOpen(false);
      setConfirmationDeleteInvoice(false);
      setIsLoadingDeletion(false);
    };

    try {
      const invoices = await getInvoicesByCustomer(customer?.id);

      // Case 1: No invoices
      if (!invoices?.length) {
        try {
          await customer.markAsDeleted(deleteInvoice);
          handleSuccess();
        } catch (err) {
          handleError(err);
        }
        return;
      }

      // Case 2: Only delete customer (keep invoices)
      if (deleteInvoice === "no") {
        try {
          await removeCustomerFromInvoices(invoices);
          await bulkDeletePayments(customer.id);
          await removeCustomerFromPosTxns(customer.id);

          await customer.markAsDeleted(deleteInvoice);
          handleSuccess();
        } catch (err) {
          handleError(err);
        }
        return;
      }

      // Case 3: Delete invoices + customer
      try {
        await bulkDeleteOrdersByInvoices(invoices, "invoice");
        await batchDeleteInvoices(invoices);

        const payments = await getPaymentsByCustomer(customer.id);

        await bulkDeleteOrdersByInvoices(payments, "payment");
        await bulkDeletePaymentsV2(payments);
        await bulkDeletePosTxns(customer.id);

        await customer.markAsDeleted(deleteInvoice);
        handleSuccess();
      } catch (err) {
        handleError(err);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const handleConfirm = async () => {
    let invoices = await getInvoicesByCustomer(customer.id);

    if (setOpenDeleteAccount) {
      if (invoices?.length) {
        setConfirmationDeleteInvoice(open);
      } else {
        deleteCustomer("no");
      }
    }
  };
  const handleClose = () => {
    if (setOpenDeleteAccount) setOpenDeleteAccount(false);
    setOpen(false);
  };
  return (
    <Box>
      {children ? <Box onClick={() => setOpen(true)}>{children}</Box> : null}

      {open && (
        <ConfirmationModal
          open={open}
          onClose={() => handleClose()}
          onConfirm={handleConfirm}
          title={I18n.this_action_cannot_be_undone}
          message={
            customer.is_supplier
              ? I18n.are_you_sure_delete_suppier
              : I18n.are_you_sure_delete_customer
          }
          confirmText={I18n.delete}
          id={id}
        />
      )}

      {confirmationDeleteInvoice && (
        <ConfirmationModal
          open={confirmationDeleteInvoice}
          onClose={(e) => {
            deleteCustomer("no");
          }}
          onConfirm={(e) => {
            deleteCustomer("yes");
          }}
          message={I18n.do_you_want_to_delete_associated_invoice}
          title={I18n.this_action_cannot_be_undone}
          confirmText={I18n.yes}
          loading={isLoadingDeletion}
          cancelText={I18n.no}
          id={id}
        />
      )}
    </Box>
  );
};

export default DeleteAccount;
